import {
  Component,
  OnInit,
  OnDestroy,
  EventEmitter,
  Output,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';

import { TranslocoModule } from '@jsverse/transloco';
import { takeUntil } from 'rxjs';
import { LoadingComponent } from '../../../../../../../shared/components/loading/loading.component';
import { SepTableSelectionService } from '../../../../../../../shared/components/sep-table/services/sep-table-selection.service';
import { LicenseInfoCardComponent } from '../../../../../../payment/subscription/components/license-info-card/license-info-card.component';
import { LearningPlanEnrollmentTableComponent } from '../../../learning-plan-enrollment-table/learning-plan-enrollment-table.component';
import { EnrollmentContainerBase } from '../../../../../../../pages/portal-container/enrollment-user-creation-dialog-container/enrollment-containers/EnrollmentContainerBase';
import { UserCreationNotificationService } from '../../../../../../organization/user/services/user-creation-notification.service';
import { UserCreationContainerComponent } from '../../../../../../organization/user/components/user-creation-container/user-creation-container.component';
import { UserCsvUploadContainerComponent } from '../../../../../../organization/user/components/user-csv-upload-container/user-csv-upload-container.component';
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { LearningPlanBuilderDataService } from '../../../../services/learning-plan-builder-data.service';
import { DIALOG_SIZE_MEDIUM } from '../../../../../../../shared/components/dialog/dialog-sizes';

export enum UserCreationDialogStep {
  CREATE = 'create',
  CSV = 'csv',
}

@Component({
  selector: 'app-learning-plan-setup-enrollment',
  imports: [
    TranslocoModule,
    LoadingComponent,
    LicenseInfoCardComponent,
    LearningPlanEnrollmentTableComponent,
    UserCreationContainerComponent,
    UserCsvUploadContainerComponent,
    MatDialogModule,
  ],
  providers: [SepTableSelectionService],
  templateUrl: './learning-plan-setup-enrollment.component.html',
  styleUrl: './learning-plan-setup-enrollment.component.scss',
})
export class LearningPlanSetupEnrollmentComponent
  extends EnrollmentContainerBase
  implements OnInit, OnDestroy
{
  private readonly dataService = inject(LearningPlanBuilderDataService);
  private readonly userCreationNotificationService = inject(
    UserCreationNotificationService,
  );
  private readonly dialog = inject(MatDialog);

  @Output() canEnrollChanged: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  @Output() userCreationButtonClickEvent: EventEmitter<void> =
    new EventEmitter<void>();
  @Output() abortButtonClickEvent: EventEmitter<void> =
    new EventEmitter<void>();
  @Output() studentSuccessfullyEnrolledEvent: EventEmitter<void> =
    new EventEmitter<void>();
  @Output() addLicensesButtonClickEvent: EventEmitter<void> =
    new EventEmitter<void>();

  @ViewChild('userCreationDialogTemplate')
  userCreationDialogTemplate: TemplateRef<unknown>;

  dialogStep: UserCreationDialogStep = UserCreationDialogStep.CREATE;
  userCreationDialogSteps = UserCreationDialogStep;
  private dialogRef: MatDialogRef<unknown> | null = null;

  constructor() {
    super();
  }
  ngOnInit(): void {
    this.getStudentsEligibleForEnrollment();
    this.getAvailableLicenses();
    this.observeTableSelectionAndSyncToDataService();
  }

  getStudentsEligibleForEnrollment() {
    // Use pre-fetched users from the data service to avoid UI jumping
    void this.dataService.prefetchUsers().then((users) => {
      this.studentsEligibleForEnrollment = this.sortStudents(
        this.transformUsersToStudents(users),
      );
      this.filteredStudents = this.studentsEligibleForEnrollment;
    });
  }

  private observeTableSelectionAndSyncToDataService(): void {
    this.tableSelectionService.selection$
      .pipe(takeUntil(this.destroy$))
      .subscribe((selection) => {
        this.selectedStudents = selection.selected;
        this.dataService.setSelectedStudents(
          selection.selected.map((student) => ({ id: student.id })),
        );
        this.dataService.setLicensesToAssignCount(
          this.countSelectedStudentsWithoutLicenseAssigned(),
        );
        this.checkLicenseEligibilityForEnrollment()
          .pipe(takeUntil(this.destroy$))
          .subscribe((canEnroll) => {
            this.canEnroll = canEnroll;
            this.canEnrollChanged.emit(canEnroll);
          });
        this.pendingLicenseAssignments =
          this.countSelectedStudentsWithoutLicenseAssigned();
        this.checkLicenseEligibilityForEnrollment();
      });
  }

  enrollStudents() {
    return;
  }

  openUserCreationDialog(): void {
    this.dialogStep = UserCreationDialogStep.CREATE;
    this.dialogRef = this.dialog.open(this.userCreationDialogTemplate, {
      autoFocus: false,
      ...DIALOG_SIZE_MEDIUM,
    });
  }

  showCsvUploadContent(): void {
    this.dialogStep = UserCreationDialogStep.CSV;
  }

  showUserCreationContent(): void {
    this.dialogStep = UserCreationDialogStep.CREATE;
  }

  closeDialog(): void {
    this.dialogRef?.close();
    this.dialogRef = null;
  }

  handleUserSuccessfullyCreatedEvent(): void {
    this.userCreationNotificationService.setUserChangedNotification();
    this.dataService.invalidateUserCache();
    this.getStudentsEligibleForEnrollment();
    this.closeDialog();
  }
}
